import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { AuthService, UserService } from '@lumio/shared';
import {
  Observable,
  Subscription,
  EMPTY,
  interval,
  throwError,
  defer,
  of,
  catchError,
  mergeMap,
  map,
  finalize,
} from 'rxjs';
import { AuthFormStateService } from '../../domain/auth/auth-form-state.service';

/** Intervalo entre verificações proativas GET /users/me (só com token). */
const SESSION_POLL_MS = 5 * 60 * 1000;

function isAuthRejected(err: unknown): boolean {
  const status = (err as HttpErrorResponse)?.status;
  return status === 401 || status === 403;
}

@Injectable({ providedIn: 'root' })
export class SessionMonitorService {
  private readonly auth = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly authFormState = inject(AuthFormStateService);
  private readonly router = inject(Router);

  private pollSub?: Subscription;
  private verifyInFlight = false;

  /**
   * Polling: a cada 5 minutos chama GET /users/me sem cache.
   * Sem token não dispara request. Falha 401/403 → logout + /welcome.
   */
  startPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(SESSION_POLL_MS)
      .pipe(
        mergeMap(() => {
          if (!this.auth.isAuthenticated()) {
            return EMPTY;
          }
          return this.userService.verifySessionActive().pipe(
            catchError((e: unknown) => {
              if (isAuthRejected(e)) {
                this.invalidateSessionAndRedirect();
              }
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  }

  invalidateSessionAndRedirect(): void {
    this.userService.clearMeCache();
    this.authFormState.clear();
    this.auth.clearLocalSession();
    void this.router.navigate(['/app/welcome'], { replaceUrl: true });
  }

  private isUsersMeGet(req: HttpRequest<unknown>): boolean {
    if (req.method !== 'GET') return false;
    const u = req.url;
    return u.includes('/users/me') && !u.includes('/users/me/profile');
  }

  /**
   * 401/403 com Bearer: revalida com GET /users/me.
   * - Se /users/me OK → sessão ativa; propaga o erro original (ex.: 403 de negócio).
   * - Se /users/me retorna 401/403 → logout.
   * - A própria falha em GET /users/me → logout direto (sem novo /me).
   */
  handleFailedAuthenticatedRequest(
    err: HttpErrorResponse,
    req: HttpRequest<unknown>
  ): Observable<never> {
    if (err.status !== 401 && err.status !== 403) {
      return throwError(() => err);
    }
    const hadBearer =
      req.headers.get('Authorization')?.startsWith('Bearer ') ?? false;
    if (!hadBearer) {
      return throwError(() => err);
    }
    if (this.isUsersMeGet(req)) {
      this.invalidateSessionAndRedirect();
      return throwError(() => err);
    }
    if (this.verifyInFlight) {
      return throwError(() => err);
    }
    this.verifyInFlight = true;

    return defer(() => this.userService.verifySessionActive()).pipe(
      map(() => ({ ok: true as const })),
      catchError((verifyErr: unknown) =>
        of({ ok: false as const, verifyErr })
      ),
      mergeMap((r) => {
        if (!r.ok && isAuthRejected(r.verifyErr)) {
          this.invalidateSessionAndRedirect();
        }
        return throwError(() => err);
      }),
      finalize(() => {
        this.verifyInFlight = false;
      })
    );
  }
}
